import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { getHomeProjectsInfolist } from "../../api/BorrowerApi";
import { getMockHomeProjectsInfolist } from "../../api/mock/MockBorrowerApi";
import { convertToDollar, convertToMinFraction, formatDataProperties, handleErrorResponse } from "../../Helpers";
import { IHomeProjectsObj, IHomeProjectsView } from "../interfaces/Borrower";
import "../../styles/borrower/home.scss";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BWHomeViewAllFrame from "./BWHomeViewAllFrame";
import { Card } from "semantic-ui-react";
import BWProjectCard from "./home/BWProjectsCard";
import BCEmptyState from "../ui/BCEmptyState";
import { FeatureFlags } from "../constants/LocalServerConstants";

interface IBWHomeProjects {
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: boolean;
  setIsMessage: (value: boolean) => React.Dispatch<React.SetStateAction<boolean>>;
  errorResponse: IErrorResponse | undefined; 
  setErrorResponse: (value: boolean) => React.Dispatch<React.SetStateAction<IErrorResponse | undefined>> 
  proxyUser: IProxyUser | undefined;
}

const BWHomeProjects = ({...props} : IBWHomeProjects) => {
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [homeProjectsInfoData, setHomeProjectsInfoData] = useState([] as IHomeProjectsObj[]);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const convertToTableFormat = (projects) => {
    const projectsTable = [] as IHomeProjectsView[];
    projects.forEach(project => projectsTable.push({
      "loanNumber": project.loanNumber,
      "project": project.projectName,
      "status": project.isActive ? "Active" : "Inactive",
      "originalLoan": convertToDollar(project.loanAmountOriginal),
      "currentLoan": convertToDollar(project.loanAmountCurrent),
      "interestRate": `${convertToMinFraction(project.interestRateCurrent, 3, 3)} %`,
      "maturityDate": new Date(project.maturityDateCurrent).toLocaleDateString(),
      "interestReserve": convertToDollar(project.interestReserveCurrent),
      "budgetBalance": convertToDollar(project.remainingBudget),
      "lastOwing": convertToDollar(project.previousOwing),
      "totalOwing": convertToDollar(project.totalOwingAmount),
      "loanOfficer": <a href={`mailto: ${project.loanOfficerEmail}`} className="borrower-project-mailto">{project.loanOfficer}</a>,
      "servicingRep": <a href={`mailto: ${project.portfolioManagerEmail}`} className="borrower-project-mailto">{project.portfolioManager}</a>
    }));

    return formatDataProperties(projectsTable, [{field: "loanNumber", action: "rename", name: "Loan #"}]);
  };

  const getHomeProjectsInfoAysnc = async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockHomeProjectsInfolist() : await getHomeProjectsInfolist(props.proxyUser && props.proxyUser.id);
      if (response && response.ok) {
        props.setIsMessage(false);
        const homeProjectsInfolist = await response.json();
        const homeProjectsViews = [] as IHomeProjectsObj[];
        homeProjectsInfolist.forEach((homeProjectsInfolistItem) => {
          homeProjectsViews.push(homeProjectsInfolistItem as IHomeProjectsObj);
        });
        
        setHomeProjectsInfoData(homeProjectsViews.sort((a, b) => new Date(b.maturityDateCurrent).getTime() - new Date(a.maturityDateCurrent).getTime()));
      }
      else {
        await handleErrorResponse(response, props.setIsMessage, props.setErrorResponse);
      }
    }
    catch (error) {
      handleError();
    }

    setIsLoading(false);
  };

  const ProjectCardView = ({projects}) => {
    return (
      <React.Fragment>
        {projects && projects.length > 0 ? <Card.Group itemsPerRow={props.isGreaterThanTablet ? 2 : 1}>
          {projects.slice(0, 6).map((project, i) => <BWProjectCard project={project} index={i} />)}
        </Card.Group> 
        : <BCEmptyState
            emptyStateClass="home-content-empty-state"
            icon="warehouse"
            text="You currently have no active project"
            content="Once a loan application is approved, it will appear as a project tile in this section"
          />
        }
      </React.Fragment>
    );
  };

    return (
      <React.Fragment>
        <BWHomeViewAllFrame 
          isGreaterThanTablet={props.isGreaterThanTablet}
          isSidebar={props.isSidebar}
          isMessage={props.isMessage}
          setIsMessage={props.setIsMessage}
          errorResponse={props.errorResponse}
          setErrorResponse={props.setErrorResponse}
          proxyUser={props.proxyUser}
          isLoading={isLoading}
          breadcrumbsCurrent="Projects"
          formattedDate={convertToTableFormat(homeProjectsInfoData)}
          getApiInfoAsync={getHomeProjectsInfoAysnc}
          infolistStyleSwitch={true}
          cardViewComponents={<ProjectCardView projects={homeProjectsInfoData} />}
          emptyState={{icon: "warehouse", text: "You currently have no active project"}}
          tableSortable={["Loan #", "Project", "Status"]}
          ellipsis
          ellipsisHandlers={[{text: "Download Draw Management", action: (item) => console.log(item)}, {text: "Download Borrower Statement", action: (item) => console.log(item)}]} // TODO: functionality
        />
      </React.Fragment>
    );
  };
  
export default BWHomeProjects;